import React, { FC, useEffect, useId, useMemo, useRef, useState } from 'react'
import { TertiaryButton } from '@moonpig/launchpad-components'
import { system as s } from '@moonpig/launchpad-system'
import { css, styled } from '@moonpig/launchpad-utils'
import { useFindLocaleText } from '../../text-localisation'
import {
  trackGAEvent,
  createGenericSelectContentEvent,
} from '@moonpig/web-core-analytics'
import { sanitizeGalleryDescriptionHtml } from './sanitizeGalleryDescriptionHtml'

const collapsedContentStyles = css`
  overflow: hidden;
  /* stylelint-disable value-no-vendor-prefix */
  display: -webkit-box;
  /* stylelint-disable property-no-vendor-prefix */
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;

  h1,
  h2,
  h3,
  h4,
  h5 {
    font-weight: 300;
    font-size: 1rem;
  }
`

const DescriptionRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 0.5rem;
`

const DescriptionContent = styled.div`
  flex: 1;
  min-width: 0;
  ${collapsedContentStyles}

  h1,
  h2,
  h3,
  h4,
  h5,
  p {
    margin: 0;
  }

  :is(h1, h2, h3, h4, h5) ~ :is(h1, h2, h3, h4, h5) {
    margin: 1rem 0 0 0;
  }

  section {
    padding: 0;
  }

  a {
    text-decoration: underline;
    ${s({
      typography: 'typeButtonTextLink',
    })}
  }

  ul,
  ul li,
  ol,
  ol li {
  all: revert;
  font: inherit;
  color: inherit;
}
`

const ToggleButton = styled(TertiaryButton)`
 ${s({
   typography: 'typeBodyLabel',
   color: 'colorInteractionTextLink',
   px: 0,
   py: 0,
   minHeight: 'unset',
 })}
  cursor: pointer;
`

const expandedContentStyles = css`
  display: block;
  overflow: visible;
  -webkit-line-clamp: unset;
  max-height: none;

  h1,
  h2,
  h3,
  h4,
  h5 {
    font-weight: 700;
    font-size: 1rem;
  }
`

const expandedLayoutStyles = css`
  ${DescriptionRow} {
    display: block;
  }

  ${DescriptionContent} {
    ${expandedContentStyles}
  }
`

const DescriptionWrapper = styled.div`
  position: relative;

  &[data-expanded='true'] {
    ${expandedLayoutStyles}
  }

  @media (scripting: none) {
    ${expandedLayoutStyles}

    ${ToggleButton} {
      display: none;
    }
  }
`

type GalleryDescriptionProps = {
  description: string
  title: string
}

export const GalleryDescription: FC<GalleryDescriptionProps> = ({
  description,
  title,
}) => {
  const localiseText = useFindLocaleText()
  const contentId = useId()
  const [isExpanded, setIsExpanded] = useState(false)
  const hasPreparedContent = useRef(false)
  const isInitialMount = useRef(true)
  const descriptionContentRef = useRef<HTMLDivElement>(null)
  const toggleButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const content = descriptionContentRef.current
    const toggleButton = toggleButtonRef.current

    /* istanbul ignore if */
    if (!content) return

    if (!hasPreparedContent.current) {
      content.tabIndex = -1
      hasPreparedContent.current = true
    }

    content.toggleAttribute('inert', !isExpanded)

    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    if (isExpanded) {
      content.focus()
    } else {
      toggleButton?.focus()
    }
  }, [isExpanded])

  const sanitizedDescription = useMemo(
    () => sanitizeGalleryDescriptionHtml(description),
    [description],
  )

  const toggleExpanded = () => {
    trackGAEvent(
      createGenericSelectContentEvent({
        eventCategory: 'gallery action',
        eventAction: `${isExpanded ? 'collapse' : 'expand'} gallery description`,
        eventLabel: title,
      }),
    )
    setIsExpanded(expanded => !expanded)
  }

  return (
    <DescriptionWrapper
      data-testid="gallery-description"
      data-expanded={isExpanded}
    >
      <DescriptionRow>
        <DescriptionContent
          ref={descriptionContentRef}
          id={contentId}
          data-testid="gallery-description-content"
          /* eslint-disable-next-line react/no-danger */
          dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
        />
        <ToggleButton
          ref={toggleButtonRef}
          type="button"
          aria-controls={contentId}
          aria-expanded={isExpanded}
          onClick={toggleExpanded}
        >
          {localiseText(isExpanded ? 'find.read_less' : 'find.read_more')}
        </ToggleButton>
      </DescriptionRow>
    </DescriptionWrapper>
  )
}
